import { createFileRoute } from '@tanstack/react-router'
import { getArticleBySlug, getArticles, getSavedArticles, saveArticle, unsaveArticle, getAllArticleProgressForUser } from '@/hooks/useArticle'
import parse from 'html-react-parser'
import DOMPurify from 'dompurify'
import ArticlesSidebar from '@/components/ArticlesSidebar'
import Button from '@/components/Button'
import { updateArticleProgress } from '@/hooks/useArticle'
import { memo, useCallback, useEffect, useMemo, useRef, useState, type FC } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { UserExpActionType } from '@/hooks/useUser'
import VideoPlayer from '@/components/VideoPlayer'
import { extractHeadingsAndInjectIds } from '@/lib/articleHeadingUtils';
import Breadcrumbs from '@/components/Breadcrumbs';

import { FaBolt, FaExclamationTriangle, FaLightbulb, FaClipboardList, FaBullseye, FaBrain, FaInfoCircle, FaBookmark, FaRegBookmark, FaCheck } from 'react-icons/fa';
import { FiClock } from 'react-icons/fi';
import { motion, animate, AnimatePresence } from 'framer-motion';

const PointsCounter: FC<{ points: number }> = ({ points }) => {
  const [value, setValue] = useState(0);
  useEffect(() => {
    const controls = animate(0, points, {
      duration: 1.2,
      ease: 'easeOut',
      onUpdate: v => setValue(Math.round(v)),
    });
    return () => controls.stop();
  }, [points]);
  return (
    <span className="tabular-nums font-semibold text-emerald-600">+{value}</span>
  );
};

// Icon mapping
const iconMap = {
  'bolt': FaBolt,
  'warning': FaExclamationTriangle,
  'lightbulb': FaLightbulb,
  'clipboard': FaClipboardList,
  'target': FaBullseye,
  'brain': FaBrain,
  'info': FaInfoCircle,
};


export const Route = createFileRoute('/_auth/articles/$slug')({
  component: RouteComponent,
})

function cleanQuillHtml(html: string) {
  // Remove Quill UI spans
  html = html.replace(/<span class="ql-ui">.*?<\/span>/g, '');

  // Convert bracket tags to styled divs
  html = html.replace(/\[TIP\](.*?)\[\/TIP\]/gi, '<div class="tip-box">$1</div>');
  html = html.replace(/\[ALERT\](.*?)\[\/ALERT\]/gi, '<div class="red-alert">$1</div>');
  html = html.replace(/\[FACT\](.*?)\[\/FACT\]/gi, '<div class="callout-box">$1</div>');
  html = html.replace(/\[SUMMARY\](.*?)\[\/SUMMARY\]/gi, '<div class="summary-box">$1</div>');
  html = html.replace(/\[TAKEAWAYS\](.*?)\[\/TAKEAWAYS\]/gi, '<div class="takeaways-box">$1</div>');
  html = html.replace(/\[QUIZ\](.*?)\[\/QUIZ\]/gi, '<div class="quiz-box">$1</div>');
  html = html.replace(/\[PLACEHOLDER\](.*?)\[\/PLACEHOLDER\]/gi, '<div class="placeholder">$1</div>');
  // Icon placeholders: [ICON:bolt] => <span data-gg-icon="bolt"></span>
  html = html.replace(/\[ICON:([a-z0-9_-]+)\]/gi, '<span style="margin-top:-3px;" data-gg-icon="$1"></span>');

  // Remove empty paragraphs (including those with <br>, NBSP, zero-width chars, and empty spans)
  if (typeof window !== 'undefined') {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    doc.body.querySelectorAll('p').forEach(p => {
      // Skip if paragraph contains obvious content nodes
      if (p.querySelector('img, video, iframe, input, button, audio, canvas, svg')) {
        return;
      }
      const normalizedInner = p.innerHTML
        .replace(/<span[^>]*>\s*<\/span>/gi, '')
        .replace(/<br\s*\/?>(\s|&nbsp;|\u00A0)*/gi, '')
        .replace(/[\u00A0\u200B\u200C\u200D]/g, '')
        .replace(/&nbsp;/gi, '')
        .trim();
      const textOnly = (p.textContent || '')
        .replace(/[\u00A0\u200B\u200C\u200D]/g, '')
        .trim();
      if (normalizedInner === '' && textOnly === '') {
        p.remove();
      }
    });
    html = doc.body.innerHTML;
  } else {
    // Fallback: broad regex removal when DOM not available
    html = html.replace(/<p>(?:\s|&nbsp;|\u00A0|<br\s*\/?>(?:\s|&nbsp;|\u00A0)*|<span[^>]*>(?:\s|&nbsp;|\u00A0|<br\s*\/?>(?:\s|&nbsp;|\u00A0)*)<\/span>)*<\/p>/gi, '');
  }

  return html;
}

const MemoizedSidebar = memo(function SidebarWrapper({
  articles,
  currentArticle,
  headings,
}: {
  articles: any[] | undefined
  currentArticle: any | undefined
  headings: { label: string; id: string }[]
}) {
  const sidebarArticles = useMemo(
    () => articles?.map(a => ({ ...a, thumbnail: a.thumbnail ?? undefined })),
    [articles],
  )
  const sidebarCurrent = useMemo(
    () => currentArticle ? { ...currentArticle, thumbnail: currentArticle.thumbnail ?? undefined } : undefined,
    [currentArticle],
  )
  const quickLinks = useMemo(
    () => headings.map(h => ({ label: h.label, href: `#${h.id}` })),
    [headings],
  )
  return (
    <ArticlesSidebar
      articles={sidebarArticles}
      currentArticle={sidebarCurrent}
      quickLinks={quickLinks}
    />
  )
})

function RouteComponent() {
  const navigate = useNavigate()
  const { data: article, isLoading, isError, error } = getArticleBySlug(Route.useParams().slug)
  const { data: articles } = getArticles()
  const { mutate: updateArticleProgressMutation, isPending: isCompletePending, isSuccess: isCompleteSuccess } = updateArticleProgress(article?.id ?? '')
  const { data: savedArticles } = getSavedArticles()
  const { data: allProgress } = getAllArticleProgressForUser()
  const saveMutation = saveArticle()
  const unsaveMutation = unsaveArticle()
  const [activeSaveId, setActiveSaveId] = useState<string | null>(null)
  const [readProgress, setReadProgress] = useState(0)
  const [hasReachedThreshold, setHasReachedThreshold] = useState(false)
  const contentRef = useRef<HTMLDivElement>(null)

  const alreadyCompleted = useMemo(
    () => !!(article?.id && allProgress?.some(p => p.articleId === article.id && p.completed)),
    [article?.id, allProgress],
  )

  // Track scroll progress through the article content
  useEffect(() => {
    const handleScroll = () => {
      if (!contentRef.current) return
      const rect = contentRef.current.getBoundingClientRect()
      const contentHeight = contentRef.current.scrollHeight
      const viewportHeight = window.innerHeight
      const scrolledPast = Math.max(0, -rect.top + viewportHeight)
      const progress = Math.min(1, scrolledPast / contentHeight)
      setReadProgress(progress)
      if (progress >= 0.75) setHasReachedThreshold(true)
    }

    window.addEventListener('scroll', handleScroll, { passive: true })
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [article?.id])

  const handleComplete = useCallback(() => {
    if (!article?.id || alreadyCompleted || isCompletePending) return
    updateArticleProgressMutation({ completed: true, action: { type: UserExpActionType.ARTICLE, id: article.id } })
  }, [article?.id, alreadyCompleted, isCompletePending, updateArticleProgressMutation])

  const isSaved = useMemo(
    () => !!(article?.id && savedArticles?.some(saved => saved.id === article.id)),
    [article?.id, savedArticles]
  )

  if (isLoading) {
    return (
      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-6xl px-6 py-10 sm:px-8">
          <div className="animate-pulse">
            <div className="mb-6 h-4 w-40 rounded bg-slate-200/60" />
            <div className="flex flex-col lg:flex-row gap-10">
              <div className="flex-1">
                <div className="h-8 w-3/4 rounded bg-slate-200/60 mb-4" />
                <div className="h-5 w-1/2 rounded bg-slate-200/40 mb-8" />
                <div className="flex gap-3 mb-8">
                  <div className="h-7 w-20 rounded-full bg-slate-200/40" />
                  <div className="h-7 w-24 rounded-full bg-slate-200/40" />
                </div>
                <div className="h-56 w-full rounded-2xl bg-slate-200/40 mb-10" />
                <div className="space-y-3">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <div key={i} className="h-4 rounded bg-slate-200/40" style={{ width: `${85 + (i * 7) % 15}%` }} />
                  ))}
                </div>
              </div>
              <div className="hidden lg:block w-[280px] shrink-0">
                <div className="h-5 w-24 rounded bg-slate-200/40 mb-4" />
                <div className="space-y-2">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-4 rounded bg-slate-200/40" style={{ width: `${60 + i * 10}%` }} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    const status = (error as any)?.response?.status
    if (status === 403) {
      navigate({ to: '/', replace: true, search: { invitationcode: undefined, __clerk_ticket: undefined } })
      return null
    }
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-500">Error loading article.</p>
      </div>
    )
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="text-slate-500">Article not found.</p>
      </div>
    )
  }

  const handleToggleSave = async () => {
    if (!article.id) return
    setActiveSaveId(article.id)
    try {
      if (isSaved) {
        await unsaveMutation.mutateAsync(article.id)
      } else {
        await saveMutation.mutateAsync(article.id)
      }
    } finally {
      setActiveSaveId(null)
    }
  }

  const { htmlWithAnchors, headings } = extractHeadingsAndInjectIds(
    DOMPurify.sanitize(cleanQuillHtml(article.content), { ADD_ATTR: ['class', 'data-gg-icon'] })
  );

  return (
    <>
      {/* Reading progress — thin, no background track */}
      {!alreadyCompleted && (
        <div className="fixed top-0 left-0 right-0 z-50">
          <div
            className="h-1 bg-violet-500 transition-all duration-150 ease-out"
            style={{ width: `${Math.round(readProgress * 100)}%` }}
          />
        </div>
      )}

      <div className="min-h-screen bg-white">
        <div className="mx-auto max-w-6xl px-6 py-8 sm:px-8 sm:py-10">

          <Breadcrumbs
            items={[
              { label: 'Home', href: '/' },
              { label: 'Guides', href: '/learn?tab=Guides' },
            ]}
          />

          <div className="flex flex-col lg:flex-row gap-0">

            {/* ── Article content ── */}
            <article className="flex-1 min-w-0 lg:pr-12 lg:max-w-[calc(65ch+3rem)]">

              {/* Title */}
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900 sm:text-4xl leading-[1.15]">
                {article.title}
              </h1>

              {/* Meta row */}
              <div className="mt-5 flex flex-wrap items-center gap-3">
                {article.tags && article.tags.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2">
                    {article.tags.slice(0, 2).map(tag => (
                      <span key={tag.id} className="rounded-full bg-violet-50 px-3.5 py-1.5 text-sm font-medium text-violet-700">
                        {tag.name}
                      </span>
                    ))}
                    {article.tags.length > 2 && (
                      <span className="text-sm text-slate-400">+{article.tags.length - 2}</span>
                    )}
                  </div>
                )}
                {typeof article.readTime === 'number' && (
                  <>
                    {article.tags && article.tags.length > 0 && <span className="h-1 w-1 rounded-full bg-slate-300" />}
                    <span className="inline-flex items-center gap-1.5 text-sm text-slate-500">
                      <FiClock className="h-4 w-4" />
                      {article.readTime} min read
                    </span>
                  </>
                )}
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <button
                  onClick={handleToggleSave}
                  disabled={activeSaveId === article.id || saveMutation.isPending || unsaveMutation.isPending}
                  className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer disabled:opacity-50 ${
                    isSaved
                      ? 'bg-violet-50 text-violet-700 hover:bg-violet-100'
                      : 'border border-slate-200 text-slate-600 hover:bg-slate-50'
                  }`}
                >
                  {isSaved ? <FaBookmark className="h-3.5 w-3.5" /> : <FaRegBookmark className="h-3.5 w-3.5" />}
                  {isSaved ? 'Saved' : 'Save'}
                </button>
              </div>

              {/* Description */}
              {article.description && (
                <p className="mt-6 text-lg leading-relaxed text-slate-600">
                  {article.description}
                </p>
              )}

              {/* Banner */}
              {article.banner && article.articleType !== 'VIDEO' && (
                <div className="mt-8">
                  <img
                    src={article.banner}
                    alt={`Banner for ${article.title}`}
                    className="w-full h-auto rounded-2xl object-cover"
                    onError={e => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                  />
                </div>
              )}

              {/* Video */}
              {article.articleType === 'VIDEO' && article.file && (
                <div className="mt-8">
                  <VideoPlayer src={article.file} poster={article.thumbnail || undefined} />
                </div>
              )}

              {/* Download */}
              {article.file && (article.articleType === 'DOWNLOADABLE_GENERIC' || article.articleType === 'DOWNLOADABLE_PDF') && (
                <div className="mt-8 flex justify-center">
                  <Button
                    href={article.file}
                    variant="outline-purple"
                    size="large"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Download Resource
                  </Button>
                </div>
              )}

              {/* Article body */}
              <div className="mt-10" ref={contentRef}>
                <div className="prose prose-purple max-w-none quill-content text-lg text-slate-900 leading-relaxed">
                  {parse(htmlWithAnchors, {
                    replace: (domNode: any) => {
                      if (domNode && domNode.type === 'tag' && domNode.name === 'span') {
                        const iconName = domNode.attribs?.['data-gg-icon'];
                        if (iconName) {
                          const IconComponent = (iconMap as any)[String(iconName).toLowerCase()];
                          if (IconComponent) {
                            return <IconComponent className="inline-block text-violet-600 align-middle" size={18} aria-hidden="true" style={{ marginTop: '-3px' }} />;
                          }
                        }
                      }
                      return undefined;
                    }
                  })}
                </div>
              </div>

              {/* Completion card */}
              <div className={`mt-10 rounded-2xl p-6 transition-all duration-300 ${
                alreadyCompleted || isCompleteSuccess
                  ? 'bg-emerald-50'
                  : hasReachedThreshold
                  ? 'bg-white shadow-[0_0_0_1px_rgba(0,0,0,0.03),0_2px_4px_rgba(0,0,0,0.04)]'
                  : 'bg-slate-100/60'
              }`}>
                {alreadyCompleted || isCompleteSuccess ? (
                  <AnimatePresence>
                    <motion.div
                      className="flex items-center gap-4"
                      initial={isCompleteSuccess && !alreadyCompleted ? { opacity: 0, y: 8 } : false}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, ease: 'easeOut' }}
                    >
                      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-emerald-100">
                        <FaCheck className="text-emerald-600 text-sm" />
                      </div>
                      <div className="flex-1">
                        <p className="text-base font-semibold text-slate-900">Article complete</p>
                        {article.exp ? (
                          <p className="text-sm text-slate-500">
                            {isCompleteSuccess && !alreadyCompleted
                              ? <>You earned <PointsCounter points={article.exp} /> Guardian Points</>
                              : `You earned ${article.exp} Guardian Points for reading this guide`
                            }
                          </p>
                        ) : (
                          <p className="text-sm text-slate-500">Nice work — you've completed this guide.</p>
                        )}
                      </div>
                      {isCompleteSuccess && !alreadyCompleted && article.exp ? (
                        <motion.div
                          className="shrink-0 flex items-center gap-1 bg-emerald-100 text-emerald-700 px-3 py-1.5 rounded-full text-sm font-semibold"
                          initial={{ scale: 0, opacity: 0 }}
                          animate={{ scale: 1, opacity: 1 }}
                          transition={{ delay: 0.3, type: 'spring', stiffness: 400, damping: 20 }}
                        >
                          +{article.exp} GP
                        </motion.div>
                      ) : null}
                    </motion.div>
                  </AnimatePresence>
                ) : hasReachedThreshold ? (
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    <div className="flex-1">
                      <p className="text-base font-semibold text-slate-900">Finished reading?</p>
                      <p className="text-sm text-slate-500">
                        Mark this guide as complete{article.exp ? ` to earn ${article.exp} Guardian Points` : ''}.
                      </p>
                    </div>
                    <Button
                      variant="primary"
                      size="large"
                      onClick={handleComplete}
                      isLoading={isCompletePending}
                      disabled={isCompletePending}
                      className="rounded-xl"
                    >
                      Mark as complete
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-slate-200/60">
                      <FaCheck className="text-slate-400 text-sm" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-400">Keep reading to complete</p>
                      <p className="text-xs text-slate-400">
                        Read through the guide to unlock completion{article.exp ? ` and earn ${article.exp} GP` : ''}.
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </article>

            {/* ── Sidebar ── */}
            <MemoizedSidebar articles={articles} currentArticle={article} headings={headings} />

          </div>
        </div>
      </div>
    </>
  )
}
